import { useState, useEffect  } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  Filter,
  X,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";
import { toast } from "sonner";
import {
  getDesignations,
  DesignationApi,
  deleteDesignation,
  createDesignation,
  updateDesignation,
} from "services/designationService";
import { useScreenPermission } from "hooks/useScreenPermission";
import Pagination from "ui/pagination";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";

interface Designation {
  id: string;
  publicId: string;
  designationCode: string;
  name: string;
  status: 'active' | 'inactive';
}

export default function DesignationMasterPage() {
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [editingItem, setEditingItem] = useState<Designation | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingDesignation, setDeletingDesignation] =
    useState<Designation | null>(null);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [designations, setDesignations] = useState<Designation[]>([]);
  const [loading, setLoading] = useState(false);

  const { screenName, icon: ScreenIcon } = useScreenPermission();

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filter states
  const [filters, setFilters] = useState({
    name: "",
    designation: "all",
    status: "all",
  });

  const [formData, setFormData] = useState({
    designation: "",
    name: "",
    status: "active" as "active" | "inactive",
  });

  const [errors, setErrors] = useState({
    designation: "",
    name: "",
  });

  const fetchDesignations = async () => {
  try {
    setLoading(true);

    const res = await getDesignations({
      searchText: searchTerm || null,
      pageNo,
      pageSize,
    });

  const mapped: Designation[] = res.items.map((item: DesignationApi) => ({
    id: item.id.toString(),
    publicId: item.publicId,
    designationCode: item.designationCode,
    name: item.designationName,
    status: item.status ? "active" : "inactive",
    }));

    setDesignations(mapped);
    setTotalCount(res.totalCount);
    } catch {
      toast.error("Failed to fetch designations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageHeader({ 
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    fetchDesignations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, searchTerm]);

  useEffect(() => {
    setPageNo(1);
  }, [searchTerm]);

  // Apply filters and search
  const filteredDesignations = designations.filter((designation) => {
    const matchesSearch =
      designation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      designation.designationCode.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesName =
      !filters.name ||
      designation.name.toLowerCase().includes(filters.name.toLowerCase());

    const matchesStatus =
      filters.status === "all" || designation.status === filters.status;

    return matchesSearch && matchesName && matchesStatus;
  });

  // Apply sorting
  const sortedDesignations = [...filteredDesignations].sort((a, b) => {
    if (!sortColumn) return 0;

    let aVal = a[sortColumn as keyof Designation];
    let bVal = b[sortColumn as keyof Designation];

    if (typeof aVal === "string") aVal = aVal.toLowerCase();
    if (typeof bVal === "string") bVal = bVal.toLowerCase();

    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      designation: "",
      name: "",
      status: "active",
    });
    setErrors({
      designation: "",
      name: "",
    });
    setShowDialog(true);
  };

  const handleEdit = (designation: Designation) => {
    setEditingItem(designation);
    setFormData({
      designation: designation.designationCode,
      name: designation.name,
      status: designation.status,
    });
    setErrors({
      designation: "",
      name: "",
    });
    setShowDialog(true);
  };

  const validateForm = () => {
    const newErrors = {
      designation: "",
      name: "",
    };

    if (!formData.designation) {
      newErrors.designation = "Designation is required";
    }

    if (!formData.name) {
      newErrors.name = "Designation name is required";
    }

    setErrors(newErrors);
    return !newErrors.designation && !newErrors.name;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const payloadBase = {
        designationCode: formData.designation,
        designationName: formData.name,
        status: formData.status === "active",
      };

      if (editingItem) {
        await updateDesignation({
          ...payloadBase,
          publicId: editingItem.publicId,
          updatedBy: "1",
        });

        toast.success("Designation updated successfully");
      } else {
        await createDesignation({
          ...payloadBase,
          createdBy: "1",
        });

        toast.success("Designation created successfully");
      }

      fetchDesignations();

      setShowDialog(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
        const apiMessage =
        error?.response?.data?.message || "Failed to create designation";

        toast.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (designation: Designation) => {
    setDeletingDesignation(designation);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingDesignation) return;

    try {
      await deleteDesignation(deletingDesignation.publicId, "1");

      toast.success("Designation deleted successfully");

      setDesignations((prev) =>
        prev.filter((d) => d.publicId !== deletingDesignation.publicId)
      );

      setIsDeleteDialogOpen(false);
      setDeletingDesignation(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const msg =
        error?.response?.data?.message || "Failed to delete designation";
      toast.error(msg);
    }
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        <>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center w-full gap-4">
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
  
                <Input
                  placeholder="Search designations..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPageNo(1);
                  }}
                  className="pl-10"
                />
              </div>
              <Button
                variant={showFilters ? "default" : "outline"}
                size="icon"
                onClick={() => setShowFilters(!showFilters)}
                style={showFilters ? { background: "#5B9BD5" } : {}}
                className={
                  showFilters
                    ? "text-white"
                    : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
                }
                title="Toggle Advanced Filters"
              >
                <Filter className="w-5 h-5" />
              </Button>
              <Button
                onClick={handleAdd}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Designation
              </Button>
            </div>
          </div>

          {/* Advanced Filters Section */}
          {showFilters && (
            <Card
              className="border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50"
              style={{ gap: "0px" }}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle
                      style={{ color: "#5B9BD5" }}
                      className="flex items-center gap-2 text-lg"
                    >
                      <Filter className="w-5 h-5" />
                      Advanced Filters
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Filter designations by multiple criteria
                    </CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFilters(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Designation Name Filter */}
                  <div className="space-y-2">
                    <Label htmlFor="filter-name" className="text-sm">
                      Designation Name
                    </Label>
                    <Input
                      id="filter-name"
                      placeholder="Search by name..."
                      value={filters.name}
                      onChange={(e) =>
                        setFilters({ ...filters, name: e.target.value })
                      }
                      className="h-9 text-sm"
                    />
                  </div>

                  {/* Status Filter */}
                  <div className="space-y-2">
                    <Label htmlFor="filter-status" className="text-sm">
                      Status
                    </Label>
                    <Select
                      value={filters.status}
                      onValueChange={(value) =>
                        setFilters({ ...filters, status: value })
                      }
                    >
                      <SelectTrigger id="filter-status" className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active Only</SelectItem>
                        <SelectItem value="inactive">Inactive Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Active Filters Summary & Clear Button */}
                {(filters.name ||
                  filters.designation !== "all" ||
                  filters.status !== "all") && (
                  <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#5B9BD5] mb-2">
                        Active Filters:
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {filters.name && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            Name: {filters.name}
                          </Badge>
                        )}
                        {filters.designation !== "all" && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            Designation: {filters.designation}
                          </Badge>
                        )}
                        {filters.status !== "all" && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            Status: {filters.status}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFilters({
                          name: "",
                          designation: "all",
                          status: "all",
                        });
                      }}
                      className="text-red-600 border-red-300 hover:bg-red-50 flex-shrink-0"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear All
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Designations Table */}
          <Card className="bg-white border-[#5B9BD5]/30" style={{ marginTop: "10px", gap: "0px" }}>
            <CardContent style={{ padding: "0px 0px 10px 0px" }}>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                        onClick={() => handleSort("designationCode")}
                      >
                        <div className="flex items-center gap-1">
                          Code
                          {sortColumn === "designationCode" ? (
                            sortDirection === "asc" ? (
                              <ArrowUp className="w-3 h-3" />
                            ) : (
                              <ArrowDown className="w-3 h-3" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3 h-3 opacity-30" />
                          )}
                        </div>
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                        onClick={() => handleSort("name")}
                      >
                        <div className="flex items-center gap-1">
                          Designation Name
                          {sortColumn === "name" ? (
                            sortDirection === "asc" ? (
                              <ArrowUp className="w-3 h-3" />
                            ) : (
                              <ArrowDown className="w-3 h-3" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3 h-3 opacity-30" />
                          )}
                        </div>
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs"
                      >
                        Status
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs text-right"
                      >
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedDesignations.length > 0 ? (
                      sortedDesignations.map((designation) => (
                        <TableRow
                          key={designation.id}
                          className="hover:bg-gray-50"
                        >
                          <TableCell className="px-4 py-3 text-xs">
                            <Badge className="bg-blue-100 text-blue-700 border border-blue-300 text-xs px-2">
                              {designation.designationCode}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-4 py-3 text-xs font-medium">
                            {designation.name}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-xs">
                            {designation.status === "active" ? (
                              <Badge className="bg-green-100 text-green-700 border border-green-300 text-xs px-2">
                                Active
                              </Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-700 border border-gray-300 text-xs px-2">
                                Inactive
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-xs text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(designation)}
                                className="h-8 px-2 hover:bg-sky-50"
                              >
                                <Edit2 className="w-4 h-4 text-[#5B9BD5]" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(designation)}
                                className="h-8 px-2 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ): (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                          No designation found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
            <Pagination
              totalItems={totalCount}
              itemsPerPage={pageSize}
              currentPage={pageNo}
              onPageChange={(page) => setPageNo(page)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPageNo(1);
              }}
              label="designations"
            />
          </Card>

          {/* Designation Dialog */}
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogContent className="max-w-lg bg-white">
              <DialogHeader>
                <DialogTitle>
                  {editingItem ? "Edit Designation" : "Add New Designation"}
                </DialogTitle>
                <DialogDescription>
                  {editingItem
                    ? "Update the designation details below"
                    : "Fill in the details to create a new designation"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <InputFieldset
                    label="code"
                    required
                    id="designation"
                    value={formData.designation}
                    onChange={(e) => {
                      setFormData({ ...formData, designation: e.target.value });
                      setErrors({ ...errors, designation: "" });
                    }}
                    placeholder="e.g., IT, HR"
                  />
                  {errors.designation && (
                    <p className="text-red-500 text-xs">{errors.designation}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Designation Name"
                    required
                    id="name"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      setErrors({ ...errors, name: "" });
                    }}
                    placeholder="e.g., IT Manager, Software Developer"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs">{errors.name}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <SelectFieldset label="Status">
                    <Select
                      value={formData.status}
                      onValueChange={(value: "active" | "inactive") =>
                        setFormData({ ...formData, status: value })
                      }
                    >
                      <SelectTriggerUI id="status">
                        <SelectValue />
                      </SelectTriggerUI>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  style={{ background: "#5B9BD5" }}
                  className="text-white"
                >
                  {editingItem ? "Update" : "Create"} Designation
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Designation Dialog */}
          <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <DialogContent className="max-w-md bg-white">
              <DialogHeader>
                <DialogTitle className="text-red-600">
                  Delete Designation
                </DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete{" "}
                  <span className="font-semibold">
                    "{deletingDesignation?.name}"
                  </span>
                  ? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsDeleteDialogOpen(false);
                    setDeletingDesignation(null);
                  }}
                >
                  Cancel
                </Button>

                <Button
                  onClick={confirmDelete}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      </div>
    </>
  );
}
